// src/components/Booking/BookingSuccess.jsx
import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    fetchCheckoutSession,
    resetSession,
    selectSessionData,
    selectSessionStatus,
} from "../Paneltours/PanelTourSlice/PanelTourSlice";
import "../styles/Booking.css";

const CCY_SYMBOL = {
    usd: "$", eur: "€", gbp: "£", cad: "CA$", aud: "A$",
    jpy: "¥", inr: "₹", sgd: "S$", hkd: "HK$", chf: "Fr",
    nzd: "NZ$", mxn: "MX$", brl: "R$", aed: "AED ",
};

const fmtAmount = (amount, currency = "usd") => {
    const sym = CCY_SYMBOL[currency?.toLowerCase()] ?? "$";
    return `${sym}${Number(amount).toFixed(2)}`;
};

export default function BookingSuccess() {
    const dispatch    = useDispatch();
    const sessionData = useSelector(selectSessionData);
    const status      = useSelector(selectSessionStatus);
    const didFetch    = useRef(false);

    useEffect(() => {
        if (didFetch.current) return;

        const params = new URLSearchParams(window.location.search);

        // FareHarbor redirects back to your success URL.
        // In FH Dashboard → Settings → Checkout → Redirect URL, set:
        //   https://yoursite.com/booking/success
        //
        // FareHarbor appends its own query params (booking_id, item_id, etc.)
        // but NOT your custom ?ref= param.
        //
        // To pass your internalRef through, use FH's "redirect_url" Lightframe
        // parameter OR store it in sessionStorage before redirecting:
        //   sessionStorage.setItem("vk_booking_ref", internalRef)
        //   → read it here after FH redirects back
        //
        // Both strategies are handled below (fallback chain):

        // Strategy A: ?ref=VK-xxx in URL (if FH preserves it)
        const refFromUrl = params.get("ref");

        // Strategy B: sessionStorage (set before opening Lightframe)
        const refFromStorage = sessionStorage.getItem("vk_booking_ref");

        // Strategy C: ?session_id=mongoId (legacy Stripe param reused)
        const sessionId = params.get("session_id");

        const internalRef = refFromUrl || refFromStorage || null;

        if (!internalRef && !sessionId) {
            // Nothing to look up — FH redirect without any identifier
            // This happens when FH doesn't pass through custom params.
            // In this case, show a generic "booking received" message.
            console.warn("[BookingSuccess] No ref or sessionId found in URL.");
            return;
        }

        didFetch.current = true;

        // Clean URL
        window.history.replaceState({}, "", "/booking/success");

        // Clean up sessionStorage
        if (refFromStorage) sessionStorage.removeItem("vk_booking_ref");

        dispatch(fetchCheckoutSession({ sessionId, internalRef }));

        return () => { dispatch(resetSession()); };
    }, [dispatch]);

    const isLoading = status === "idle" || status === "loading";

    return (
        <div className="vkv-shell">
            <div className="vkv-card">

                {/* ── LOADING ── */}
                {isLoading && (
                    <>
                        <div className="vkv-spinner" />
                        <div className="vkv-eyebrow">// CONFIRMING BOOKING</div>
                        <div className="vkv-title">
                            STAND<br /><span className="orange">BY...</span>
                        </div>
                        <p className="vkv-sub">Verifying your booking with FareHarbor...</p>
                    </>
                )}

                {/* ── SUCCESS ── */}
                {status === "succeeded" && sessionData && (
                    <>
                        <div className="vkv-check">✓</div>
                        <div className="vkv-eyebrow orange">// BOOKING CONFIRMED</div>
                        <div className="vkx-title">
                            SLOT<br /><span className="orange">SECURED</span>
                        </div>

                        <p className="vkv-sub">
                            Your pre-order for <strong>{sessionData.tourName}</strong> is confirmed.
                            {sessionData.email && (
                                <> Confirmation sent to{" "}
                                    <span className="blue">{sessionData.email}</span>.
                                </>
                            )}
                        </p>

                        <div className="vkv-driver-card">
                            <div className="vkv-dc-header">
                                <span className="vkv-dc-logo">VK</span>
                                <span className="vkv-dc-badge">BOOKING CONFIRMED</span>
                                <span className="vkv-dc-status">PAID</span>
                            </div>

                            <div className="vkv-dc-callsign">
                                {sessionData.callsign
                                    ? `[${sessionData.callsign}] ${sessionData.tourName}`
                                    : sessionData.tourName}
                            </div>

                            <div className="vkv-dc-rows">
                                {[
                                    ["TOUR",         sessionData.tourName,                                  ""],
                                    ["AMOUNT",       fmtAmount(sessionData.amount, sessionData.currency),   "blue"],
                                    ["CURRENCY",     (sessionData.currency ?? "usd").toUpperCase(),         "blue"],
                                    ["DRIVERS",      sessionData.qty || "1",                                ""],
                                    ["BOOKING REF",  sessionData.internalRef || "—",                       ""],
                                    ["DEPLOYMENT",   "WINTER 2026",                                         "blue"],
                                    ["STATUS",       sessionData.status === "paid" ? "CONFIRMED" : "PENDING",
                                                     sessionData.status === "paid" ? "lime" : "orange"],
                                ].map(([label, value, cls]) => (
                                    <div key={label} className="vkv-dc-row">
                                        <span className="vkv-dc-label">{label}</span>
                                        <span className={`vkv-dc-val${cls ? ` ${cls}` : ""}`}>{value}</span>
                                    </div>
                                ))}
                            </div>

                            <div className="vkv-dc-footer">VEGAS KARTS LLC · LAS VEGAS, NV</div>
                        </div>

                        <a href="/" className="vkv-btn">BACK TO BASE ▶</a>
                    </>
                )}

                {/* ── NO REF FOUND (FH redirect without params) ── */}
                {status === "idle" && !sessionData && (
                    <>
                        <div className="vkv-check">✓</div>
                        <div className="vkv-eyebrow orange">// BOOKING RECEIVED</div>
                        <div className="vkx-title">
                            SLOT<br /><span className="orange">RESERVED</span>
                        </div>
                        <p className="vkv-sub">
                            Your booking was submitted. You will receive a confirmation
                            email from FareHarbor shortly.
                        </p>
                        <a href="/" className="vkv-btn">BACK TO BASE ▶</a>
                    </>
                )}

                {/* ── FAILED ── */}
                {status === "failed" && (
                    <>
                        <div className="vkv-error-icon">⚠</div>
                        <div className="vkv-eyebrow" style={{ color: "#FFB800" }}>// COULD NOT CONFIRM</div>
                        <div className="vkv-title">SESSION<br />NOT FOUND</div>
                        <p className="vkv-sub">
                            Your payment may still have gone through. Check your email for a
                            FareHarbor confirmation. If you were charged, contact us.
                        </p>
                        <a href="/" className="vkv-btn vkv-btn--ghost">BACK TO HOME ▶</a>
                    </>
                )}

            </div>
        </div>
    );
}